import React, { useEffect, useState } from 'react'
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import { Link, useHistory, useLocation } from 'react-router-dom'

import Moment from 'moment'

import NewAppointment from '../../../containers/NewAppointment'
import TaskPage from '../../../containers/TaskPage'
import AppointmentService from '../../../services/AppointmentsService'
import CasesService from '../../../services/CasesService'
import format from '../../../services/format'
import TasksService from '../../../services/TasksService'
import Permissions, { CAPABILITIES } from '../../../utils/Permissions'
import CalendarAgendaEventComponent from '../../calendar/CalendarAgendaEventComponent'
import CalendarCustomToolbar from '../../calendar/CalendarCustomToolbar'
import CalendarEventComponent from '../../calendar/CalendarEventComponent'
import FAIcon from '../../FAIcon'

import 'react-big-calendar/lib/sass/styles.scss'
import '../../../containers/CalendarPage.scss'
import './TimelineTab.scss'

const localizer = momentLocalizer(Moment)
const ALL_CALENDAR_VIEWS = { month: true, week: true, day: true, agenda: true }

export default function TimelineTab({
  caseNumber,
  user,
  showError,
  incompleteTasks,
  completeTasks,
  refreshCaseStatus,
}) {
  const location = useLocation()
  const history = useHistory()
  const isTaskRoute = location.pathname.includes('/tasks')
  const isAppointmentRoute = location.pathname.includes('/appointments')

  const [caseModel, setCaseModel] = useState(null)
  const [appointments, setAppointments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [viewMode, setViewMode] = useState(() => {
    const params = new URLSearchParams(location.search || '')
    return params.get('view') === 'calendar' ? 'calendar' : 'list'
  })
  const [calendarView, setCalendarView] = useState(Views.MONTH)

  const handleViewModeChange = nextMode => {
    setViewMode(nextMode)
    const params = new URLSearchParams(location.search || '')
    if (nextMode === 'calendar') {
      params.set('view', 'calendar')
    } else {
      params.delete('view')
    }
    const queryStr = params.toString()
    history.replace({
      pathname: location.pathname,
      search: queryStr ? `?${queryStr}` : '',
    })
  }

  useEffect(() => {
    if (isTaskRoute || isAppointmentRoute) {
      return // Don't fetch timeline data if inline forms are rendered
    }

    setIsLoading(true)
    Promise.all([CasesService.get(caseNumber), AppointmentService.getCaseAppointments(caseNumber)])
      .then(([caseData, appointmentsData]) => {
        setCaseModel(caseData)
        setAppointments(appointmentsData || [])
        setIsLoading(false)
      })
      .catch(err => {
        if (showError) showError(err.message)
        setIsLoading(false)
      })
  }, [caseNumber, isTaskRoute, isAppointmentRoute, incompleteTasks, completeTasks, showError])

  if (isTaskRoute) {
    return <TaskPage caseManager={user} isInline={true} refreshCaseStatus={refreshCaseStatus} />
  }

  if (isAppointmentRoute) {
    return <NewAppointment isInline={true} refreshCaseStatus={refreshCaseStatus} />
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading Timeline...</span>
        </div>
      </div>
    )
  }

  // Combine and sort events
  const events = []

  if (caseModel) {
    if (caseModel.injury?.date) {
      events.push({
        id: 'injury-date',
        date: caseModel.injury.date,
        title: 'Date of Injury',
        description: 'The date on which the claimant sustained injuries.',
        type: 'milestone',
        icon: 'briefcase-medical',
        color: '#e056fd', // Premium vibrant purple
      })
    }
    if (caseModel.referral?.date) {
      events.push({
        id: 'referral-date',
        date: caseModel.referral.date,
        title: 'Referral Date',
        description: 'The date this case was referred to GuideSmart.',
        type: 'milestone',
        icon: 'handshake',
        color: '#10ac84', // Mint green
      })
    }
    if (caseModel.createdUtc) {
      events.push({
        id: 'created-date',
        date: caseModel.createdUtc,
        title: 'Case Created',
        description: 'Case record initialized in GuideSmart.',
        type: 'milestone',
        icon: 'folder-plus',
        color: '#576574', // Slate
      })
    }
    if (caseModel.intakeCompletedUtc) {
      events.push({
        id: 'activated-date',
        date: caseModel.intakeCompletedUtc,
        title: 'Case Activated',
        description: 'Intake completed and case set active.',
        type: 'milestone',
        icon: 'play',
        color: '#2ed573', // Vibrant green
      })
    }
    if (caseModel.caseClosedTimeStamp) {
      events.push({
        id: 'closed-date',
        date: caseModel.caseClosedTimeStamp,
        title: 'Case Closed',
        description: 'Case processing finalized and archived.',
        type: 'milestone',
        icon: 'lock',
        color: '#2f3542', // Dark grey
      })
    }
  }

  // Add Tasks
  const allTasks = [...(incompleteTasks || []), ...(completeTasks || [])]
  allTasks.forEach(task => {
    const rawNoteOrDesc = (task.note || task.description || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    const cleanTitle = (task.title || '').replace(/\s+/g, ' ').trim()
    const taskCardDescription =
      rawNoteOrDesc && rawNoteOrDesc.toLowerCase() !== cleanTitle.toLowerCase() ? rawNoteOrDesc : ''

    events.push({
      id: `task-${task.taskNumber}`,
      date: task.dueDateUtc || task.createdUtc,
      title: task.title,
      description: taskCardDescription,
      type: 'task',
      completed: task.complete,
      link: `/cases/${caseNumber}/tasks/${task.taskNumber}`,
      icon: 'check-circle',
      color: task.complete ? '#2ed573' : '#1e90ff', // Blue or Green
      originalItem: task,
    })
  })

  // Add Appointments
  appointments.forEach(appointment => {
    const providerDesc =
      [appointment.individualProvider, appointment.provider].filter(Boolean).join(' / ') ||
      'No provider details specified.'

    events.push({
      id: `appointment-${appointment.appointmentNumber}`,
      date: appointment.startUtc,
      title: appointment.title,
      description: providerDesc,
      type: 'appointment',
      completed: appointment.complete,
      link: `/cases/${caseNumber}/appointments/${appointment.appointmentNumber}`,
      icon: 'calendar-alt',
      color: appointment.complete ? '#2ed573' : '#ff4757', // Coral red or Green
      originalItem: appointment,
    })
  })

  // Add Surgeries / Hospitalizations (related to injury) as Completed Appointments
  const surgeries = caseModel?.surgeriesAndHospitalizations || []
  surgeries
    .filter(s => s.relatedToInjury)
    .forEach((surgery, idx) => {
      const surgeryDate = surgery.dateOfSurgery || surgery.dateAdmitted || surgery.dateDischarged
      if (surgeryDate) {
        const surgeryProviderDesc =
          [surgery.individualProvider, surgery.provider].filter(Boolean).join(' / ') || 'No provider details specified.'

        events.push({
          id: `surgery-${idx}`,
          date: surgeryDate,
          title: surgery.title || 'Surgery/Hospitalization',
          description: surgeryProviderDesc,
          type: 'appointment',
          completed: true,
          link: `/cases/${caseNumber}/details?section=5`,
          icon: 'procedures',
          color: '#2ed573', // Green for completed
          originalItem: surgery,
        })
      }
    })

  // Sort descending chronologically, with unscheduled items at the top
  events.sort((a, b) => {
    if (!a.date && !b.date) return 0
    if (!a.date) return -1
    if (!b.date) return 1
    return new Date(b.date) - new Date(a.date)
  })

  const handleToggleTask = async (task, e) => {
    e.preventDefault()
    e.stopPropagation()
    const updated = { ...task, complete: !task.complete }
    try {
      await TasksService.update(updated, caseNumber, task.taskNumber)
      if (refreshCaseStatus) {
        refreshCaseStatus()
      }
    } catch (err) {
      if (showError) showError(err.message)
    }
  }

  const convertUtcToLocalDateTime = (utcDateTime, appointment) => {
    if (!utcDateTime) return null
    if (appointment?.isAllDay) {
      return Moment(Moment.utc(utcDateTime).format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day').toDate()
    }
    return Moment.utc(utcDateTime).local().toDate()
  }

  const calendarEvents = []
  appointments.forEach(a => {
    if (!a.startUtc) return
    const isAllDay = Boolean(a.isAllDay && a.startUtc)
    const startDate = convertUtcToLocalDateTime(a.startUtc, a)
    if (!startDate) return
    let endDate = a.endUtc ? convertUtcToLocalDateTime(a.endUtc, a) : null
    if (!endDate || endDate < startDate) {
      endDate = isAllDay ? startDate : Moment(startDate).add(1, 'hour').toDate()
    }
    calendarEvents.push({
      id: a.appointmentNumber,
      caseNumber: Number(caseNumber),
      title: a.title,
      isComplete: a.complete,
      start: startDate,
      end: endDate,
      allDay: isAllDay,
      type: 'Appointment',
      link: `/cases/${caseNumber}/appointments/${a.appointmentNumber}`,
      resource: a,
    })
  })

  allTasks.forEach(t => {
    const rawDate = t.dueDateUtc || t.createdUtc
    if (!rawDate) return
    const date = Moment(Moment.utc(rawDate).format('YYYY-MM-DD'), 'YYYY-MM-DD').toDate()
    calendarEvents.push({
      id: t.taskNumber,
      caseNumber: Number(caseNumber),
      title: t.title,
      isComplete: Boolean(t.complete || t.completed),
      start: date,
      end: date,
      allDay: true,
      type: 'Task',
      link: `/cases/${caseNumber}/tasks/${t.taskNumber}`,
      resource: t,
    })
  })

  const currentReturnPath = `${location.pathname}${viewMode === 'calendar' ? '?view=calendar' : ''}`

  const onSelectCalendarEvent = event => {
    if (event.link) {
      history.push({
        pathname: event.link,
        state: { from: currentReturnPath },
      })
    }
  }

  const calendarEventPropGetter = event => {
    let className = ''
    const isOverdue = event.type === 'Task' && !event.isComplete && Moment.utc(event.start) < Moment.utc()
    if (event.type === 'Task') {
      if (event.isComplete) className = 'task-event-completed'
      else if (isOverdue) className = 'task-overdue'
      else className = 'task-event'
    } else {
      className = 'appointment-event'
    }
    return { className }
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center my-1">
          <h4 className="text-dark font-weight-bold mb-0 mr-3">Case Timeline</h4>
          <div className="btn-group btn-group-sm" role="group" aria-label="Timeline view mode">
            <button
              type="button"
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleViewModeChange('list')}
            >
              <FAIcon name="stream" className="mr-1" /> Timeline
            </button>
            <button
              type="button"
              className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleViewModeChange('calendar')}
            >
              <FAIcon name="calendar-alt" className="mr-1" /> Calendar
            </button>
          </div>
        </div>
        <div className="timeline-actions d-flex align-items-center flex-wrap my-1">
          {Permissions.can(user, CAPABILITIES.CREATE_APPOINTMENT) && (
            <Link
              to={{
                pathname: `/cases/${caseNumber}/appointments`,
                state: { from: currentReturnPath },
              }}
              className="btn btn-outline-primary btn-sm mr-2"
            >
              <FAIcon name="plus-circle" className="mr-1" /> New Appointment
            </Link>
          )}
          {Permissions.can(user, CAPABILITIES.CREATE_TASK) && (
            <Link
              to={{
                pathname: `/cases/${caseNumber}/tasks`,
                state: { from: currentReturnPath },
              }}
              className="btn btn-outline-primary btn-sm"
            >
              <FAIcon name="plus-circle" className="mr-1" /> New Task
            </Link>
          )}
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <div className="calendar-page-container">
          <Calendar
            className="calendar-component shadow mb-4"
            localizer={localizer}
            formats={{ timeGutterFormat: 'h A' }}
            events={calendarEvents}
            views={ALL_CALENDAR_VIEWS}
            view={calendarView}
            onView={setCalendarView}
            defaultDate={Moment().startOf('day').toDate()}
            length={90}
            startAccessor="start"
            endAccessor="end"
            onSelectEvent={onSelectCalendarEvent}
            eventPropGetter={calendarEventPropGetter}
            components={{
              toolbar: CalendarCustomToolbar,
              month: {
                event: CalendarEventComponent,
              },
              week: {
                event: CalendarAgendaEventComponent,
              },
              day: {
                event: CalendarAgendaEventComponent,
              },
              agenda: {
                event: CalendarAgendaEventComponent,
              },
            }}
          />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center text-muted py-5">
          <FAIcon name="history" type="fas fa-3x" className="mb-3 text-light" />
          <p className="lead">No events have been recorded for this case yet.</p>
        </div>
      ) : (
        <div className="timeline-wrapper">
          <div className="timeline-line"></div>
          {events.map(event => {
            const isTask = event.type === 'task'
            const isMilestone = event.type === 'milestone'

            return (
              <div key={event.id} className="timeline-item">
                <div className="timeline-badge-container">
                  <div className="timeline-badge" style={{ backgroundColor: event.color }}>
                    <FAIcon name={event.icon} type="fas text-white" />
                  </div>
                </div>

                <div className="timeline-card-container">
                  <div className="timeline-card">
                    <div className="timeline-card-header d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                        {isTask && (
                          <button
                            className="btn btn-link p-0 mr-2 text-decoration-none timeline-checkbox flex-shrink-0"
                            onClick={e => handleToggleTask(event.originalItem, e)}
                          >
                            <FAIcon
                              name={event.completed ? 'check-square' : 'square'}
                              type={event.completed ? 'fas text-success fa-lg' : 'far text-muted fa-lg'}
                            />
                          </button>
                        )}
                        <h5 className="timeline-card-title mb-0">
                          {isMilestone ? (
                            <span className="font-weight-bold">{event.title}</span>
                          ) : (
                            <Link to={event.link} className="timeline-title-link">
                              {event.title}
                            </Link>
                          )}
                        </h5>
                        {!isMilestone && event.link && (
                          <Link
                            to={event.link}
                            className="btn btn-link btn-sm p-0 ml-2 text-secondary flex-shrink-0"
                            title={`Edit ${event.type}`}
                          >
                            <FAIcon name="pencil-alt" />
                          </Link>
                        )}
                      </div>
                      <span className="timeline-card-date">
                        {event.date && Moment(event.date).isValid()
                          ? Moment(event.date).format('MMM D, YYYY')
                          : 'To be scheduled'}
                      </span>
                    </div>

                    <div className="timeline-card-body">
                      {Boolean(event.description) && <p className="mb-0 text-muted">{event.description}</p>}
                      {event.type === 'appointment' && (
                        <div className="mt-2 text-details d-flex justify-content-between align-items-center flex-wrap">
                          <div>
                            {event.date && format.time(event.date) ? (
                              <span className="badge badge-light mr-2">{format.time(event.date)}</span>
                            ) : null}
                            {event.completed ? (
                              <span className="badge badge-success">Completed</span>
                            ) : (
                              <span className="badge badge-danger">Upcoming</span>
                            )}
                          </div>
                          {event.originalItem?.appointmentNumber &&
                            Permissions.can(user, CAPABILITIES.CREATE_APPOINTMENT) && (
                              <Link
                                to={{
                                  pathname: `/cases/${caseNumber}/appointments`,
                                  search: `?duplicateFrom=${event.originalItem.appointmentNumber}`,
                                  state: {
                                    duplicateAppointment: event.originalItem,
                                    from: currentReturnPath,
                                  },
                                }}
                                className="btn btn-outline-primary btn-sm py-0 px-2 duplicate-timeline-appt-btn"
                                title="Duplicate this appointment"
                              >
                                <FAIcon name="copy" className="mr-1" /> Duplicate
                              </Link>
                            )}
                        </div>
                      )}
                      {event.type === 'task' && (
                        <div className="mt-2 text-details d-flex justify-content-between align-items-center flex-wrap">
                          <div>
                            {event.completed ? (
                              <span className="badge badge-success">Completed</span>
                            ) : (
                              <span className="badge badge-primary">Open Task</span>
                            )}
                          </div>
                          {event.originalItem?.taskNumber && Permissions.can(user, CAPABILITIES.CREATE_TASK) && (
                            <Link
                              to={{
                                pathname: `/cases/${caseNumber}/tasks`,
                                search: `?duplicateFrom=${event.originalItem.taskNumber}`,
                                state: {
                                  duplicateTask: event.originalItem,
                                  from: currentReturnPath,
                                },
                              }}
                              className="btn btn-outline-primary btn-sm py-0 px-2 duplicate-timeline-task-btn"
                              title="Duplicate this task"
                            >
                              <FAIcon name="copy" className="mr-1" /> Duplicate
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
